import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Spin, Alert } from 'antd';
import { authActions } from '../../../state/features/authReducer';
import { RootState } from '../../../state/features/reducers';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from '@reduxjs/toolkit';

/**
 * Handles the OAuth/OIDC/SAML callback after SSO login.
 * Reads code and state from URL, exchanges for tokens, then redirects.
 */
const SSOCallbackHandler: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useDispatch<ThunkDispatch<RootState, unknown, AnyAction>>();
    const error = useSelector((x: RootState) => x.auth?.error);
    const user = useSelector((x: RootState) => x.auth?.user);
    const loading = useSelector((x: RootState) => x.auth?.loading);

    useEffect(() => {
        const code = searchParams.get('code');
        const state = searchParams.get('state');

        if (!code || !state) {
            dispatch(authActions.clearError());
            // No code or state - might be error from IdP, user cancelled, or invalid callback
            const errorParam = searchParams.get('error');
            const errorDesc = searchParams.get('error_description');
            if (errorParam) {
                console.warn('SSO error from IdP:', errorParam, errorDesc);
            }
            navigate('/', { replace: true });
            return;
        }

        dispatch(
            authActions.handleSSOCallback({
                code,
                state,
            })
        );
    }, [searchParams, dispatch, navigate]);

    // On success, authReducer navigate will handle redirect
    useEffect(() => {
        if (user && !loading) {
            navigate('/', { replace: true });
        }
    }, [user, loading, navigate]);

    if (loading) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    gap: 16,
                }}
            >
                <Spin size="large" />
                <span>Completing sign in...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    padding: 24,
                }}
            >
                <Alert
                    type="error"
                    message="Sign in failed"
                    description={error}
                    showIcon
                    style={{ maxWidth: 480 }}
                />
                <a
                    href="/"
                    style={{ marginTop: 16 }}
                    onClick={(e) => {
                        e.preventDefault();
                        navigate('/', { replace: true });
                    }}
                >
                    Return to login
                </a>
            </div>
        );
    }

    return null;
};

export default SSOCallbackHandler;
